import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Scrollbars from 'react-custom-scrollbars-2';
import cn from 'classnames';

import OwnControls from 'components/Interview/OnlineInterview/RightPanel/Participants/Controls/OwnControls';
import ModeratorControls from 'components/Interview/OnlineInterview/RightPanel/Participants/Controls/ModeratorControls';
import ParticipantControls from 'components/Interview/OnlineInterview/RightPanel/Participants/Controls/ParticipantControls';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import Avatar from 'components/Interview/OnlineInterview/RightPanel/Participants/Avatar';

import { OnlineInterviewContext } from 'context/OnlineInterview/OnlineInterviewContext';
import { UserContext } from 'context/UserContext';

import getUserAvatarURL from 'components/common/getUserAvatarURL';
import { isModerator, isObserver, isRespondent, isTranslator } from 'constants/UserRoles';
import useCustomTooltip from 'hooks/common/useTooltip';

import styles from './Participants.module.scss';

function Participants() {
    const { usersState, interviewUser, isOwner, kickUser, closeKickModal, kickModal, timeout } = useContext(
        OnlineInterviewContext,
    );
    const { user } = useContext(UserContext);
    const { t } = useTranslation();

    if (!usersState || !interviewUser) return;

    return (
        <div className={styles.participants}>
            <div className={styles.listContainer}>
                <Scrollbars>
                    {[...usersState.users, ...timeout]
                        .filter((u) => (isOwner || user.isSuperAdmin || !isObserver(u.role)) && !u.isSuperAdmin)
                        .map((user) => (
                            <Participant user={user} key={user.id} />
                        ))}
                </Scrollbars>
            </div>
            {kickModal.isOpened && (
                <ConfirmDialog
                    onConfirm={() => kickUser(kickModal.user.id)}
                    onClose={closeKickModal}
                    title={t('onlineInterview:kickModalTitle').replace('%username%', kickModal.user.firstName)}
                    text={
                        !kickModal.user.isOwner &&
                        t('onlineInterview:kickModalText').replace('%username%', kickModal.user.firstName)
                    }
                />
            )}
        </div>
    );
}

const Participant = ({ user }) => {
    const { t } = useTranslation();
    const {
        interviewUser,
        changeCapabilities,
        isOwner,
        openKickModal,
        changeSoundEnabled,
        setLanguageToListen,
        editingName,
        setEditingName,
        setName,
        moveToWaitingRoom,
    } = useContext(OnlineInterviewContext);
    const { tooltip, hoverFunctions } = useCustomTooltip(user.displayName, styles.nameTooltip);
    const isCurrentUserObserver = isObserver(interviewUser.role);
    const isCurrentUser = user.id === interviewUser.id;
    const isStreamingAudio = user.capabilities.audio && user.devicePermissions.audio;
    const isSoundEnabled = !interviewUser.mutedUsers.includes(user.id);
    const changeUserSoundEnabled = (isEnabled) => {
        changeSoundEnabled(user.id, isEnabled);
        if (isCurrentUserObserver) setLanguageToListen('Custom');
    };
    const isInterviewObserver = isObserver(user.role);
    const role = user.isOwner ? 'Moderator' : user.role;
    const isEditingName = user.id === editingName.user;
    const onEditNameToggle = (value) => {
        if (value) {
            return setEditingName(user.id, user.displayName);
        }

        setName(user.id, editingName.value);
    };
    const onNameChange = (value) => {
        setEditingName(user.id, value);
    };

    const nameClassName = cn(styles.name, {
        [styles.withControls]: isOwner && !isCurrentUser,
        [styles.ownerControls]: isOwner && isCurrentUser,
    });
    const onMoveToWaitingRoom = () => {
        moveToWaitingRoom(user.id);
    };
    const isSharing = user.devicePermissions.screenSharing && user.capabilities.screenSharing;
    const isTimeout = user.timeout;

    return (
        <div key={user.id} className={styles.participantContainer}>
            <div className={styles.participant}>
                <Avatar avatarURL={getUserAvatarURL(user)} isSharing={isSharing} />
                <div className={styles.info}>
                    <div className={nameClassName} {...hoverFunctions}>
                        <Username
                            name={user.displayName || ''}
                            isEditing={isEditingName}
                            onNameChange={onNameChange}
                            nameValue={editingName.value}
                            onEditNameToggle={onEditNameToggle}
                        />
                    </div>
                    {user.displayName && user.displayName.length >= 10 && tooltip}
                    <div className={styles.role}>{t(`roles.${role.toLowerCase()}`)}</div>
                </div>
                {isCurrentUser && !isInterviewObserver && (
                    <OwnControls isOwner={isOwner} isEditingName={isEditingName} onEditNameToggle={onEditNameToggle} />
                )}
                {(isOwner || interviewUser.isSuperAdmin) && !isCurrentUser && !isInterviewObserver && (
                    <ModeratorControls
                        capabilities={user.capabilities}
                        changeCapabilities={(capabilities) => changeCapabilities(user.id, capabilities)}
                        isStreamingAudio={isStreamingAudio}
                        isSoundEnabled={isSoundEnabled}
                        changeSoundEnabled={changeUserSoundEnabled}
                        isEditingName={isEditingName}
                        onEditNameToggle={onEditNameToggle}
                        onKick={() => openKickModal(user)}
                        moveToWaitingRoom={onMoveToWaitingRoom}
                        isTimeout={isTimeout}
                        isChangeAudioCapabilitiesAvailable={!isTranslator(user.role)}
                        isModerator={isModerator(role)}
                    />
                )}
                {!isOwner && !isCurrentUser && !isRespondent(interviewUser.role) && !interviewUser.isSuperAdmin && (
                    <ParticipantControls
                        isStreamingAudio={isStreamingAudio}
                        isSoundEnabled={isSoundEnabled}
                        changeSoundEnabled={changeUserSoundEnabled}
                    />
                )}
            </div>
            <div className={styles.divider}>
                <div className={styles.avatar} />
                <div className={styles.rest} />
            </div>
        </div>
    );
};

export const Username = ({ isEditing, name, onNameChange, nameValue, onEditNameToggle }) => {
    const inputRef = useRef(null);
    useEffect(() => {
        if (isEditing) inputRef.current.focus();
    }, [isEditing]);

    if (!isEditing) return name;

    const handleChange = ({ target: { value } }) => onNameChange(value);
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') onEditNameToggle(false);
    };

    return (
        <input
            onKeyDown={handleKeyPress}
            value={nameValue}
            onChange={handleChange}
            className={styles.nameInput}
            ref={inputRef}
        />
    );
};

export default React.memo(Participants);
